import React, { Suspense } from 'react';
import { apm } from '@elastic/apm-rum';
import dayjs from 'dayjs';
import { Flex } from 'glints-aries/es';
import { Badge, Typography } from 'glints-aries/es/@next';
import { space8 } from 'glints-aries/es/@next/utilities/spacing';
import { get, merge } from 'lodash-es';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { updateUser } from '../../../actions/entities';
import { useAxiosWithAPI } from '../../../common/hooks/useAxiosWithAPI';
import useGetFeatureCount from '../../../common/hooks/useGetFeatureCount';
import ErrorBoundaryWithCrashReporting from '../../../components/Error/ErrorBoundaryWithCrashReporting';
import GlintsVIPComponentWrapper from '../../../components/GlintsVIPComponentWrapper';
import { User } from '../../../models/User';
import { getUser } from '../../../selectors/user';
import useCompanyShortlistQuotaModal from '../ShortlistQuotaModal/hooks/useCompanyShortlistQuotaModal';

const messages = defineMessages({
  companyShortlistQuota: {
    id: 'text-company-shortlist-quota',
    defaultMessage: 'Company Shortlist Quota: {quota}',
  },
  unlimited: {
    id: 'text-unlimited',
    defaultMessage: 'Unlimited',
  },
  newTag: {
    id: 'modules.Onboarding.CompanySelect.new',
    defaultMessage: 'NEW',
  },
});

type CompanyShortlistQuotaMenuItemProps = {
  closePopover: () => void;
};

function CompanyShortlistQuotaMenuItemComponent({
  closePopover,
}: CompanyShortlistQuotaMenuItemProps) {
  const intl = useIntl();
  const { companyShortlistQuotaModal, openCompanyShortlistQuotaModal } =
    useCompanyShortlistQuotaModal();
  const { companyShortlistQuota } = useGetFeatureCount();
  const quotaValue = companyShortlistQuota?.isUnlimited
    ? intl.formatMessage(messages.unlimited)
    : intl.formatNumber(companyShortlistQuota?.totalRemaining ?? 0);

  const user: User = useSelector(getUser);
  const hasViewedItem = get(
    user.metadata || {},
    'empCompanyShortlistQuotaNudge',
    false
  );
  const dispatch = useDispatch();
  const axios = useAxiosWithAPI();

  async function markNudgeAsViewed() {
    if (hasViewedItem) return;
    const metadataToUpdate = {
      empCompanyShortlistQuotaNudge: dayjs().toISOString(),
    };
    const mergedMetadata = merge(
      { ...(user.metadata as Record<string, unknown>) },
      metadataToUpdate
    ) as Record<string, unknown>;

    try {
      void axios.put('/me', {
        data: {
          metadata: metadataToUpdate,
        },
      });
    } catch (error: unknown) {
      if (error instanceof Error) {
        apm.captureError(error);
      }
    }

    dispatch(
      updateUser(user.id, {
        metadata: mergedMetadata,
      })
    );
  }

  const handleClosePopover = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    closePopover();
    markNudgeAsViewed();
    openCompanyShortlistQuotaModal();
  };

  const content = (
    <Flex
      style={{
        padding: space8,
        gap: space8,
        cursor: 'pointer',
      }}
      alignItems="center"
    >
      <img
        src="/images/contact-packs/contact-pack-icon.svg"
        alt=""
        width={24}
        height={24}
      />
      <Typography variant="body1">
        <FormattedMessage
          {...messages.companyShortlistQuota}
          values={{
            quota: (
              <Typography variant="body2" as="span">
                {quotaValue}
              </Typography>
            ),
          }}
        />
      </Typography>
      <If condition={!hasViewedItem}>
        <Badge status="enticing">{intl.formatMessage(messages.newTag)}</Badge>
      </If>
    </Flex>
  );

  return (
    <>
      <div style={{ cursor: 'pointer' }} onClick={handleClosePopover}>
        {content}
      </div>
      {companyShortlistQuotaModal}
    </>
  );
}

export default function CompanyShortlistQuotaMenuItem(
  props: CompanyShortlistQuotaMenuItemProps
) {
  return (
    <GlintsVIPComponentWrapper>
      <ErrorBoundaryWithCrashReporting fallback={null}>
        <Suspense fallback={null}>
          <CompanyShortlistQuotaMenuItemComponent {...props} />
        </Suspense>
      </ErrorBoundaryWithCrashReporting>
    </GlintsVIPComponentWrapper>
  );
}
